/* eslint-disable import/prefer-default-export */
import { DEFAULT_EXTRACTED_PACKING_LIST_FORM_VALUES } from "./constants";

const updatePackingListItems = (
  item: any,
  { default_currency, defaultHandlingUnit }: { default_currency?: string; defaultHandlingUnit?: string },
) => ({
  ...item,
  customs_info_enabled: !!item.good_customs_information,
  good_customs_information: item.good_customs_information?.map((info: any) => ({
    ...info,
    currency_code: info.currency || default_currency || "", // Fallback to empty string if currency is undefined
  })),
  height: item.height || 1,
  length: item.length || 1,
  width: item.width || 1,
  quantity: item.quantity || 0,
  weight_piece: item.weight_piece || 0,
  handling_unit: item.handling_unit || defaultHandlingUnit || "",
  crating_required: false,
});

export function getDefaultExtractedPackingListFormValues({
  extractedPackingListDetails,
  default_currency,
  defaultHandlingUnit,
}: {
  extractedPackingListDetails?: any;
  default_currency?: string;
  defaultHandlingUnit?: string;
}) {
  const { draft_data, payment_location_reference_number, request_type } = extractedPackingListDetails || {};
  const isQuickRequest = request_type === "quickrequest";

  const updatedPackingList = (draft_data?.packing_list || []).map((item: any) =>
    updatePackingListItems(item, { default_currency, defaultHandlingUnit }),
  );
  const { freight_mode } = draft_data || {};

  const output = {
    ...DEFAULT_EXTRACTED_PACKING_LIST_FORM_VALUES,
    ...(freight_mode && {
      invited_modes: {
        [freight_mode]: true,
      },
    }),
    packing_list: updatedPackingList,
    service: {
      incoterms: draft_data?.incoterms || draft_data?.service?.incoterms || "None",
    },
    organizational: {
      location: payment_location_reference_number || draft_data?.organizational?.location || "",
    },
  };
  const { invited_modes, ...rest } = output;

  if (isQuickRequest) return rest;
  return output;
}

type DimensionUnit = "cm" | "in";
type WeightUnit = "kg" | "lbs" | "total_kg" | "total_lbs";

interface Item {
  height: number;
  length: number;
  width: number;
  m_unit: DimensionUnit;
  weight_piece: number;
  w_unit: WeightUnit;
  units: number;
}

const CM_TO_M = 0.01;
const IN_TO_M = 0.0254;

function toMeters(value: number, unit: DimensionUnit): number {
  return unit === "cm" ? value * CM_TO_M : value * IN_TO_M;
}

export function calculateTotals(items: Item[]) {
  return items.reduce(
    (totals, item) => {
      const lengthM = toMeters(item.length, item.m_unit);
      const widthM = toMeters(item.width, item.m_unit);
      const heightM = toMeters(item.height, item.m_unit);

      const cbmPerUnit = lengthM * widthM * heightM;
      const lineCbm = cbmPerUnit * item.units;

      const lineWeight = ["total_kg", "total_lbs"].includes(item.w_unit)
        ? item.weight_piece
        : item.weight_piece * item.units;

      totals.totalCbm += lineCbm;
      totals.totalWeight += lineWeight;
      totals.weightUnit = item.w_unit === "total_kg" ? "kg" : item.w_unit === "total_lbs" ? "lbs" : item.w_unit;

      return totals;
    },
    {
      totalCbm: 0,
      totalWeight: 0,
      weightUnit: "kg",
    },
  );
}
